import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Upload, Star, StarOff, Sparkles, Loader2, Split } from "lucide-react";
import { toast } from "sonner";

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", category: "ortodoncia", before_image: "", after_image: "", is_real: true });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/gallery");
      setItems(r.data);
    } catch { toast.error("Error cargando galería"); }
  };
  useEffect(() => { load(); }, []);

  const onFile = async (key, file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Imagen demasiado grande (máx 4MB)"); return; }
    const data = await fileToDataUrl(file);
    setForm((f) => ({ ...f, [key]: data }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.before_image || !form.after_image) { toast.error("Completa título y ambas imágenes"); return; }
    setSaving(true);
    try {
      await api.post("/gallery", form);
      toast.success("Caso publicado");
      setForm({ title: "", description: "", category: "ortodoncia", before_image: "", after_image: "", is_real: true });
      load();
    } catch { toast.error("Error al guardar"); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar este caso?")) return;
    await api.delete(`/gallery/${id}`);
    setItems(items.filter((i) => i.id !== id));
  };

  const toggleReal = async (g) => {
    try {
      await api.patch(`/gallery/${g.id}/real`, { is_real: !g.is_real });
      toast.success(!g.is_real ? "Marcado como Caso Real ⭐" : "Quitado de Casos Reales");
      load();
    } catch { toast.error("Error al actualizar"); }
  };

  const realCount = items.filter((i) => i.is_real).length;
  const unfiltered = items.filter((i) => !i.brand_filter_applied);
  const unfilteredOrto = unfiltered.filter((i) => i.category === "ortodoncia");
  const [bulkRunning, setBulkRunning] = useState(false);

  const bulkApplyFilter = async (category) => {
    const target = category === "ortodoncia" ? unfilteredOrto : unfiltered;
    if (!target.length) { toast.info("No hay casos sin filtro de marca."); return; }
    const n = Math.min(target.length, 10);
    if (!confirm(`Aplicar filtro de marca (Nano Banana) a los ${n} casos más recientes ${category === "ortodoncia" ? "de Ortodoncia" : ""} sin estilizar?\nPuede tardar 2-4 min.`)) return;
    setBulkRunning(true);
    try {
      const r = await api.post("/gallery/bulk-restyle", { category: category || null, limit: 10 });
      toast.success(`Filtro aplicado: ${r.data.succeeded}/${r.data.processed} OK${r.data.failed ? ` · ${r.data.failed} fallaron` : ""}`);
      load();
    } catch (e) {
      toast.error("Error al aplicar filtro masivo");
    } finally {
      setBulkRunning(false);
    }
  };

  // ---------- Combined upload (auto-split) ----------
  const [combined, setCombined] = useState({
    image: "", title: "", description: "", category: "estetica",
    orientation: "auto", swap: false, is_real: true,
  });
  const [combinedBusy, setCombinedBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const setCombinedImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Debe ser una imagen"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Imagen demasiado grande (máx 10MB)"); return; }
    const data = await fileToDataUrl(file);
    setCombined((c) => ({ ...c, image: data }));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) setCombinedImage(f);
  };

  const submitCombined = async (e) => {
    e.preventDefault();
    if (!combined.image || !combined.title) {
      toast.error("Necesitas título e imagen combinada");
      return;
    }
    setCombinedBusy(true);
    try {
      const r = await api.post("/gallery/upload-combined", {
        image_base64: combined.image,
        title: combined.title,
        description: combined.description,
        category: combined.category,
        orientation: combined.orientation,
        swap: combined.swap,
        is_real: combined.is_real,
      });
      toast.success(`Caso guardado (partido ${r.data.orientation_used === "horizontal" ? "arriba/abajo" : "izq/der"})`);
      setCombined({ image: "", title: "", description: "", category: "estetica",
                    orientation: "auto", swap: false, is_real: true });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al partir/guardar");
    } finally {
      setCombinedBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="gallery-panel">
      <div className="lg:col-span-5 space-y-6">
        {/* Auto-split dropzone (combined before/after in ONE image) */}
        <form
          onSubmit={submitCombined}
          className="bg-[#1A1C1A] text-[#F4F0EB] rounded-2xl border border-[#1A1C1A] p-7 space-y-4"
          data-testid="gallery-combined-form"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Subida rápida</div>
              <h3 className="font-display text-2xl leading-tight">Antes & Después<br />en una sola imagen</h3>
              <p className="text-[11px] text-[#A69076] mt-2 leading-relaxed">
                Arrastra una captura combinada. Detecto la orientación y la divido automáticamente.
              </p>
            </div>
            <Split className="w-6 h-6 text-[#D97757] flex-shrink-0 mt-1" />
          </div>

          <label
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`relative cursor-pointer block aspect-video rounded-xl border-2 border-dashed transition-colors overflow-hidden flex items-center justify-center ${
              dragOver ? "border-[#D97757] bg-[#2a2b29]" : "border-[#3a3b39] bg-[#2a2b29] hover:border-[#A69076]"
            }`}
          >
            {combined.image ? (
              <img src={combined.image} alt="" className="absolute inset-0 w-full h-full object-contain" />
            ) : (
              <div className="text-center text-[#A69076] flex flex-col items-center gap-2 px-4">
                <Upload className="w-8 h-8" />
                <span className="text-xs uppercase tracking-widest">Arrastra o click para subir</span>
                <span className="text-[10px] normal-case text-[#5C5E5C]">JPG/PNG · hasta 10 MB</span>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="gallery-combined-file"
              onChange={(e) => setCombinedImage(e.target.files?.[0])}
            />
          </label>

          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase text-[#A69076] font-normal mb-2 block">Título</Label>
            <Input
              value={combined.title}
              onChange={(e) => setCombined({ ...combined, title: e.target.value })}
              placeholder="Ej. Rinomodelación sin cirugía"
              className="bg-[#2a2b29] border-[#3a3b39] text-[#F4F0EB] placeholder:text-[#5C5E5C]"
              data-testid="gallery-combined-title"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase text-[#A69076] font-normal mb-2 block">Categoría</Label>
              <Select value={combined.category} onValueChange={(v) => setCombined({ ...combined, category: v })}>
                <SelectTrigger className="bg-[#2a2b29] border-[#3a3b39] text-[#F4F0EB]" data-testid="gallery-combined-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ortodoncia">Ortodoncia</SelectItem>
                  <SelectItem value="estetica">Medicina Estética</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase text-[#A69076] font-normal mb-2 block">Orientación</Label>
              <Select value={combined.orientation} onValueChange={(v) => setCombined({ ...combined, orientation: v })}>
                <SelectTrigger className="bg-[#2a2b29] border-[#3a3b39] text-[#F4F0EB]" data-testid="gallery-combined-orientation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (recomendado)</SelectItem>
                  <SelectItem value="vertical">Vertical · Izq/Der</SelectItem>
                  <SelectItem value="horizontal">Horizontal · Arriba/Abajo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs text-[#A69076] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={combined.swap}
              onChange={(e) => setCombined({ ...combined, swap: e.target.checked })}
              className="accent-[#D97757]"
              data-testid="gallery-combined-swap"
            />
            La imagen tiene el <b className="text-[#F4F0EB]">DESPUÉS a la izquierda / arriba</b> (invertir)
          </label>

          <Button
            type="submit"
            disabled={combinedBusy || !combined.image || !combined.title}
            className="w-full rounded-full bg-[#D97757] hover:bg-[#C66749] text-white h-11 text-xs uppercase tracking-[0.2em] gap-2"
            data-testid="gallery-combined-submit"
          >
            {combinedBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Procesando…</> : <><Split className="w-4 h-4" /> Partir y guardar</>}
          </Button>
        </form>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E6E2DC] p-7 space-y-5" data-testid="gallery-form">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Subida manual (2 imágenes)</div>
          <h3 className="font-display text-2xl text-[#1A1C1A]">Sube por separado</h3>
        </div>
        <div>
          <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Título</Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej. Brackets cerámicos · 14 meses" required data-testid="gallery-title" />
        </div>
        <div>
          <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Categoría</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="gallery-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ortodoncia">Ortodoncia</SelectItem>
              <SelectItem value="estetica">Medicina Estética</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Descripción</Label>
          <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="gallery-desc" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <FileSlot label="Antes" img={form.before_image} onPick={(f) => onFile("before_image", f)} testid="gallery-before" />
          <FileSlot label="Después" img={form.after_image} onPick={(f) => onFile("after_image", f)} testid="gallery-after" />
        </div>
        <Button type="submit" disabled={saving} className="rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white h-11 px-7 text-xs uppercase tracking-[0.2em]" data-testid="gallery-submit">
          {saving ? "Guardando..." : "Publicar caso"}
        </Button>
      </form>
      </div>

      <div className="lg:col-span-7">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076]">Casos publicados ({items.length})</div>
          <div className="flex items-center gap-2 flex-wrap">
            {unfiltered.length > 0 && (
              <Button
                onClick={() => bulkApplyFilter(unfilteredOrto.length ? "ortodoncia" : null)}
                disabled={bulkRunning}
                size="sm"
                className="h-8 rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white text-[10px] tracking-[0.2em] uppercase px-3 gap-1.5"
                data-testid="gallery-bulk-restyle"
              >
                {bulkRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {bulkRunning ? "Aplicando…" : `Bulk filter (${Math.min(unfiltered.length, 10)})`}
              </Button>
            )}
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#1A1C1A] bg-[#D9CDBC] px-2.5 py-1 rounded-full flex items-center gap-1">
              <Star className="w-3 h-3 fill-current" /> {realCount} en sitio público
            </div>
          </div>
        </div>
        {items.length === 0 && <div className="text-sm text-[#5C5E5C] bg-white rounded-2xl border border-[#E6E2DC] p-10 text-center" data-testid="gallery-empty">Aún no hay casos publicados.</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {items.map((g) => (
            <div key={g.id} className={`bg-white rounded-2xl overflow-hidden border-2 transition-colors ${g.is_real ? "border-[#A69076]" : "border-[#E6E2DC]"}`} data-testid={`gallery-item-${g.id}`}>
              <div className="grid grid-cols-2 gap-0.5 relative">
                {g.is_real && (
                  <div className="absolute top-2 left-2 z-10 bg-[#A69076] text-white text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Star className="w-2.5 h-2.5 fill-current" /> Real
                  </div>
                )}
                <img src={g.before_image} className="aspect-square object-cover w-full" alt="Antes" />
                <img src={g.after_image} className="aspect-square object-cover w-full" alt="Después" />
              </div>
              <div className="p-4 flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076]">{g.category}</div>
                  <div className="font-display text-lg text-[#1A1C1A] truncate">{g.title}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => toggleReal(g)} title={g.is_real ? "Quitar de Casos Reales" : "Marcar como Caso Real"} className={`p-2 rounded-lg transition-colors ${g.is_real ? "text-[#A69076] hover:bg-[#F4F0EB]" : "text-[#5C5E5C] hover:bg-[#F4F0EB]"}`} data-testid={`gallery-toggle-real-${g.id}`}>
                    {g.is_real ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                  </button>
                  <button onClick={() => remove(g.id)} className="p-2 text-[#965D5D] hover:bg-[#F5E0E0] rounded-lg" data-testid={`gallery-delete-${g.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const FileSlot = ({ label, img, onPick, testid }) => (
  <div>
    <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">{label}</Label>
    <label className="relative cursor-pointer block aspect-square rounded-xl border-2 border-dashed border-[#D9CDBC] hover:border-[#A69076] bg-[#FAFAFA] overflow-hidden flex items-center justify-center">
      {img ? (
        <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="text-center text-[#A69076] flex flex-col items-center gap-2">
          <Upload className="w-6 h-6" />
          <span className="text-xs uppercase tracking-widest">Subir</span>
        </div>
      )}
      <input type="file" accept="image/*" className="hidden" data-testid={testid} onChange={(e) => onPick(e.target.files?.[0])} />
    </label>
  </div>
);
